import React from 'react';
import clsx from 'clsx';
import BlurIcon from '@mui/icons-material/BlurOnOutlined';
import { styled } from '@mui/material/styles';
import NoneIcon from '@mui/icons-material/NotInterestedOutlined';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';

export type Thumbnail = 'none' | 'blur' | 'image';

interface BackgroundThumbnailProps {
  thumbnail: Thumbnail;
  imagePath?: string;
  name?: string;
  index?: number;
}

const ThumbContainer = styled('div')({
  margin: '5px',
  width: 'calc(50% - 10px)',
  display: 'flex',
  position: 'relative',
  '&::after': {
    content: '""',
    paddingBottom: '55.5%',
  },
  '&:hover .thumb-overlay': {
    visibility: 'visible',
    background: 'rgba(95, 93, 128, 0.6)',
    color: 'white',
  },
});

const ThumbIconContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '10px',
  border: `solid ${theme.palette.grey[400]}`,
  '&.selected': {
    border: `solid ${theme.palette.primary.main}`,
    '& svg': {
      color: `${theme.palette.primary.main}`,
    },
  },
  '& svg': {
    height: 50,
    width: 50,
    color: `${theme.palette.grey[400]}`,
  },
}));

const ThumbImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  objectFit: 'cover',
  borderRadius: '10px',
  border: `solid ${theme.palette.grey[400]}`,
  '&:hover': {
    cursor: 'pointer',
  },
  '&.selected': {
    border: `solid ${theme.palette.primary.main}`,
  },
}));

const ThumbOverlay = styled('div')({
  position: 'absolute',
  color: 'transparent',
  padding: '20px',
  fontSize: '14px',
  fontWeight: 'bold',
  width: '100%',
  height: '100%',
  borderRadius: '10px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

export default function BackgroundThumbnail({ thumbnail, imagePath, name, index }: BackgroundThumbnailProps) {
  const { backgroundSettings, setBackgroundSettings } = useVideoContext();
  const isImage = thumbnail === 'image';
  const thumbnailSelected = isImage
    ? backgroundSettings.index === index && backgroundSettings.type === 'image'
    : backgroundSettings.type === thumbnail;
  const icons = {
    none: NoneIcon,
    blur: BlurIcon,
    image: null,
  };
  const ThumbnailIcon = icons[thumbnail];

  return (
    <ThumbContainer
      onClick={() =>
        setBackgroundSettings({
          type: thumbnail,
          index: index,
        })
      }
    >
      {ThumbnailIcon ? (
        <ThumbIconContainer className={clsx({ selected: thumbnailSelected })}>
          <ThumbnailIcon />
        </ThumbIconContainer>
      ) : (
        <ThumbImage className={clsx({ selected: thumbnailSelected })} src={imagePath} alt={name} />
      )}
      <ThumbOverlay className="thumb-overlay">{name}</ThumbOverlay>
    </ThumbContainer>
  );
}
